import React from "react";
import styles from "../../../../styles/AdminConsole.module.css";
import { formatDate } from "../../../../utils/utils";
import Modal from "../../shared/components/Modal";

const SAMPLE_LABELS = {
  requested: "Requested",
  "in-production": "In Production",
  delivered: "Delivered",
  approved: "Approved",
};

/**
 * SampleLogModal
 *
 * Previously rendered INSIDE the paginated row map — one instance per row,
 * per render. Now a single instance rendered outside the table entirely.
 *
 * Props:
 *   lead        object — the lead whose sample log is being viewed/edited
 *   date        string (YYYY-MM-DD)
 *   note        string
 *   saving      bool
 *   canWrite    bool  — when false the add-entry form is hidden
 *   onDateChange  (val) => void
 *   onNoteChange  (val) => void
 *   onSave      () => void
 *   onClose     () => void
 */
const SampleLogModal = React.memo(function SampleLogModal({
  lead,
  date,
  note,
  saving,
  canWrite,
  onDateChange,
  onNoteChange,
  onSave,
  onClose,
}) {
  const entries = [...(lead.sampleLog || [])].sort(
    (a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0),
  );

  return (
    <Modal
      title={`Sample Log — ${lead.buyerName}`}
      onClose={onClose}
      maxWidth={520}
    >
      {/* ── Existing entries ── */}
      <div style={{ overflowY: "auto", maxHeight: "35vh", marginBottom: 12 }}>
        {entries.length === 0 ? (
          <p style={{ padding: "16px 24px", opacity: 0.4, fontSize: 12 }}>
            No entries yet.
          </p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr className={styles.tableHeadRow}>
                <th className={styles.th}>Date</th>
                <th className={styles.th}>Stage</th>
                <th className={styles.th}>Note</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, i) => (
                <tr key={i} className={styles.tableRow}>
                  <td className={styles.td}>{formatDate(entry.date)}</td>
                  <td className={styles.td}>
                    {SAMPLE_LABELS[entry.stage] || entry.stage || "—"}
                  </td>
                  <td className={styles.td}>{entry.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Add new entry — only for writers ── */}
      {canWrite && (
        <div className={styles.formInner}>
          <div>
            <span
              className={styles.toggleLabelText}
              style={{ display: "block", marginBottom: 4, fontSize: 10 }}
            >
              Date *
            </span>
            <input
              className={styles.input}
              type="date"
              value={date}
              onChange={(e) => onDateChange(e.target.value)}
            />
          </div>
          <input
            className={styles.input}
            placeholder="Note — dispatch details, courier, remarks…"
            value={note}
            onChange={(e) => onNoteChange(e.target.value)}
          />
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={saving}
            onClick={onSave}
          >
            {saving ? "Saving…" : "Add Entry"}
          </button>
        </div>
      )}
    </Modal>
  );
});

export default SampleLogModal;
